import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Pill from '../components/ui/Pill';
import { EmptyState, ErrorNotice, InfoNotice, SkeletonRows } from '../components/ui/States';
import Checklist from '../components/Checklist';
import StipendMessage from '../components/StipendMessage';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';
import { dateShort, fmt } from '../lib/format';
import { label, money, toCents, inputClass, selectClass, PAYMENT_STATUS_LABEL, PAYMENT_TONE } from '../lib/programs';
import { errorMessage } from '../lib/printable';
import type { DataResponse, ProgramCycleList } from '../contracts';
import {
  Agreement,
  AGREEMENT_STATUS,
  CELL,
  MESSAGE_LABEL,
  NpiPack,
  ROUTE_TEMPLATE,
  STIPEND_EVIDENCE,
  StipendCounts,
  StipendGrid,
  StipendRow,
  WAITING_ON,
} from '../lib/stipends';

type MessageTarget = { kind: string; participantId?: string; agreementId?: string };

function Settings({ grid, onSaved }: { grid: StipendGrid; onSaved: () => void }) {
  const s = grid.cycle.settings;
  const [enabled, setEnabled] = useState(s.stipendsEnabled);
  const [amount, setAmount] = useState(String(s.stipendAmountCents / 100));
  const [formUrl, setFormUrl] = useState(s.stipendFormUrl ?? '');
  const cents = toCents(amount);
  const save = useMutation(
    async () =>
      api.patch(`/stipends/cycles/${grid.cycle.id}/settings`, {
        stipendsEnabled: enabled,
        stipendAmountCents: cents,
        stipendFormUrl: formUrl || null,
      }),
    { onSuccess: onSaved },
  );
  const refresh = useMutation(async () => api.post(`/stipends/cycles/${grid.cycle.id}/refresh`), {
    onSuccess: onSaved,
  });
  return (
    <Panel className="mb-5">
      <PanelHeader title="Stipend settings" note={s.stipendsEnabledExplicit ? undefined : 'default for the year'}>
        <Button size="sm" variant="ghost" disabled={refresh.isLoading} onClick={() => refresh.mutate()}>
          {refresh.isLoading ? 'Refreshing…' : 'Re-read forms and agreements'}
        </Button>
      </PanelHeader>
      <PanelBody className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <label className="flex items-center gap-2 text-table">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Stipends on for this cohort
        </label>
        <div>
          <label className="block text-small text-ink-2 mb-1">Amount per Fellow ($)</label>
          <input className={inputClass} value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div>
          <label className="block text-small text-ink-2 mb-1">Institution form link (https://)</label>
          <input className={inputClass} value={formUrl} onChange={(e) => setFormUrl(e.target.value)} />
        </div>
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            disabled={save.isLoading || cents === null || Number.isNaN(cents)}
            onClick={() => save.mutate()}
          >
            {save.isLoading ? 'Saving…' : 'Save'}
          </Button>
        </div>
        {save.error || refresh.error ? (
          <div className="md:col-span-4">
            <ErrorNotice>{errorMessage(save.error || refresh.error, 'Could not save.')}</ErrorNotice>
          </div>
        ) : null}
      </PanelBody>
    </Panel>
  );
}

function FellowPanel({
  row,
  grid,
  onClose,
  onChanged,
  onMessage,
}: {
  row: StipendRow;
  grid: StipendGrid;
  onClose: () => void;
  onChanged: () => void;
  onMessage: (t: MessageTarget) => void;
}) {
  const [route, setRoute] = useState(row.routeSource === 'override' ? row.route : '');
  const setRouteM = useMutation(
    async () => api.post(`/stipends/participants/${row.participantId}/route`, { route: route || null }),
    { onSuccess: onChanged },
  );
  const agreement = grid.agreements.find((a) => a.id === row.agreementId);
  return (
    <Modal title={row.name ?? 'Fellow'} width={860} onClose={onClose}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-table mb-5">
        <div>
          <div className="text-small text-ink-2 mb-1">Route</div>
          <div>
            {grid.routes[row.route]}
            {row.routeSource === 'override'
              ? ' (set by staff)'
              : row.routeSource === 'default'
                ? ' (until the form arrives)'
                : ' (from the form)'}
          </div>
          <div className="flex gap-2 mt-2">
            <select className={selectClass} value={route} onChange={(e) => setRoute(e.target.value)}>
              <option value="">Follow the form{row.computedRoute ? ` (${grid.routes[row.computedRoute]})` : ''}</option>
              {Object.entries(grid.routes).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              variant="outline"
              disabled={setRouteM.isLoading || route === (row.routeSource === 'override' ? row.route : '')}
              onClick={() => setRouteM.mutate()}
            >
              Set
            </Button>
          </div>
        </div>
        <div>
          <div className="text-small text-ink-2 mb-1">Supervisor</div>
          <div>
            {row.fellowKind === 'ecr'
              ? 'ECR: the Fellow is the recipient'
              : row.supervisor
                ? `${row.supervisor.name} · ${row.supervisor.email ?? ''}`
                : 'Not known yet'}
          </div>
          {row.signatoryLooksValid === false ? (
            <div className="text-small mt-1">
              <Pill tone="failed">Check the signatory title</Pill>
            </div>
          ) : null}
          {agreement ? (
            <div className="text-ink-2 text-small mt-1">
              Agreement: {agreement.participants.length} Fellow(s), {money(agreement.amountCents)}
              {agreement.batchNumber ? `, batch ${agreement.batchNumber}` : ''}
            </div>
          ) : null}
        </div>
        <div>
          <div className="text-small text-ink-2 mb-1">Ledger</div>
          {row.ledger ? (
            <div className="flex items-center gap-2">
              {money(row.ledger.amountCents)}{' '}
              <Pill tone={PAYMENT_TONE[row.ledger.status]}>{PAYMENT_STATUS_LABEL[row.ledger.status]}</Pill>
            </div>
          ) : (
            '—'
          )}
          <div className="flex gap-2 mt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onMessage({ kind: 'phase1', participantId: row.participantId })}
            >
              {MESSAGE_LABEL.phase1}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onMessage({ kind: 'form_reminder', participantId: row.participantId })}
            >
              {MESSAGE_LABEL.form_reminder}
            </Button>
          </div>
        </div>
      </div>
      {setRouteM.error ? (
        <ErrorNotice>{errorMessage(setRouteM.error, 'Could not change the route.')}</ErrorNotice>
      ) : null}
      {row.agreementId ? (
        <p className="text-small text-ink-2 mb-3">
          Steps from “Request for ToF” on are shared with every Fellow on the agreement: closing one closes it for all.
        </p>
      ) : null}
      <Checklist
        subjectType="participant"
        subjectId={row.participantId}
        mutable
        onChange={onChanged}
        evidenceFields={STIPEND_EVIDENCE}
      />
      {row.userId ? (
        <Link className="text-small underline block mt-4" to={`/analytics/members/${row.userId}`}>
          Member 360
        </Link>
      ) : null}
    </Modal>
  );
}

function Grid({
  grid,
  templateKey,
  rows,
  onOpen,
}: {
  grid: StipendGrid;
  templateKey: string;
  rows: StipendRow[];
  onOpen: (r: StipendRow) => void;
}) {
  const columns = grid.columns[templateKey] ?? [];
  return (
    <div className="overflow-x-auto">
      <table className="text-small border-collapse">
        <thead>
          <tr className="text-ink-2 align-bottom">
            <th className="text-left font-normal px-2 py-1 sticky left-0 bg-white min-w-[180px]">Fellow</th>
            {columns.map((c, i) => (
              <th key={c.key} title={c.label} className="font-normal px-1 py-1 w-[34px]">
                <div className="[writing-mode:vertical-rl] rotate-180 whitespace-nowrap max-h-[150px] overflow-hidden text-ellipsis text-small">
                  {i + 1}. {c.label}
                </div>
              </th>
            ))}
            <th className="font-normal px-2 py-1 text-left">Progress</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.participantId}
              className="border-t border-line hover:bg-paper cursor-pointer"
              onClick={() => onOpen(r)}
            >
              <td className="px-2 py-[6px] sticky left-0 bg-white">
                <div>{r.name ?? 'Deleted member'}</div>
                <div className="text-small text-ink-2">
                  {r.supervisor ? r.supervisor.name : r.fellowKind === 'ecr' ? 'ECR' : 'supervisor not known'}
                </div>
              </td>
              {columns.map((c) => {
                const cell = r.steps[c.key];
                if (!cell) return <td key={c.key} />;
                const look = CELL[cell.state];
                return (
                  <td
                    key={c.key}
                    title={`${c.label}: ${cell.overdue ? 'late' : look.label}${cell.completedAt ? ` (${dateShort(cell.completedAt)})` : cell.dueAt ? ` (due ${dateShort(cell.dueAt)})` : ''}`}
                    className={`text-center px-1 ${cell.overdue ? 'bg-warning-tint text-ink font-semibold' : look.className}`}
                  >
                    {cell.overdue ? '!' : look.glyph}
                  </td>
                );
              })}
              <td className="px-2 text-ink-2 tabular whitespace-nowrap">
                {r.progress.finished}/{r.progress.total}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Agreements({
  grid,
  onChanged,
  onMessage,
}: {
  grid: StipendGrid;
  onChanged: () => void;
  onMessage: (t: MessageTarget) => void;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const batch = useMutation(
    async (ids: string[] | null) =>
      api.post(`/stipends/cycles/${grid.cycle.id}/batches`, ids ? { agreementIds: ids } : {}),
    {
      onSuccess: () => {
        setPicked([]);
        onChanged();
      },
    },
  );
  const unbatch = useMutation(async (id: string) => api.post(`/stipends/agreements/${id}/unbatch`), {
    onSuccess: onChanged,
  });
  const toggle = (id: string) =>
    setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : picked.length < 3 ? [...picked, id] : picked);
  const ready = grid.agreements.filter((a) => a.ready).length;
  const show = (a: Agreement) =>
    a.recipientKind === 'fellow'
      ? `${a.participants[0]?.name ?? 'Fellow'} (ECR)`
      : a.supervisor
        ? a.supervisor.name
        : 'Supervisor not known yet';
  return (
    <Panel className="mb-5">
      <PanelHeader title="Agreements and batches" note={`${grid.agreements.length} agreement(s) · ${ready} ready`}>
        <Button
          size="sm"
          variant="outline"
          disabled={!picked.length || batch.isLoading}
          onClick={() => batch.mutate(picked)}
        >
          Batch selected ({picked.length}/3)
        </Button>
        <Button size="sm" variant="primary" disabled={!ready || batch.isLoading} onClick={() => batch.mutate(null)}>
          Batch next 3 ready
        </Button>
      </PanelHeader>
      <PanelBody>
        {batch.error || unbatch.error ? (
          <ErrorNotice>{errorMessage(batch.error || unbatch.error, 'Could not update the batch.')}</ErrorNotice>
        ) : null}
        {!grid.agreements.length ? (
          <EmptyState title="No agreements yet">
            Transfer of Funds Fellows get one once they are on the stipend checklist.
          </EmptyState>
        ) : (
          <table className="w-full text-small">
            <thead>
              <tr className="text-left text-small text-ink-2 border-b border-line-strong">
                <th className="py-2 pr-2" />
                <th className="py-2 pr-3">Recipient</th>
                <th className="py-2 pr-3">Fellows</th>
                <th className="py-2 pr-3">Institution / signatory</th>
                <th className="py-2 pr-3">Amount</th>
                <th className="py-2 pr-3">Period</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Batch</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {grid.agreements.map((a) => (
                <tr key={a.id} className="border-b border-line align-top">
                  <td className="py-2 pr-2">
                    {a.ready ? (
                      <input
                        type="checkbox"
                        checked={picked.includes(a.id)}
                        onChange={() => toggle(a.id)}
                        aria-label="Pick for a batch"
                      />
                    ) : null}
                  </td>
                  <td className="py-2 pr-3">
                    {show(a)}
                    <div className="text-small text-ink-2">
                      {a.recipientKind === 'fellow' ? '' : a.supervisor?.email}
                    </div>
                  </td>
                  <td className="py-2 pr-3">{a.participants.map((p) => p.name ?? 'Deleted member').join(', ')}</td>
                  <td className="py-2 pr-3">
                    {a.institutionName ?? '—'}
                    <div className="text-small text-ink-2">
                      {a.signatory
                        ? `${a.signatory.name}${a.signatory.title ? `, ${a.signatory.title}` : ''}`
                        : 'no signatory yet'}
                    </div>
                  </td>
                  <td className="py-2 pr-3 tabular">{money(a.amountCents)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {a.periodStart ? `${a.periodStart} → ${a.periodEnd}` : '—'}
                  </td>
                  <td className="py-2 pr-3">
                    <Pill tone={a.status === 'cleared' ? 'healthy' : a.status === 'preparing' ? 'planned' : 'running'}>
                      {AGREEMENT_STATUS[a.status] ?? a.status}
                    </Pill>
                    {a.ready ? <div className="text-small text-ink-2 mt-1">ready for a batch</div> : null}
                  </td>
                  <td className="py-2 pr-3">
                    {a.batchNumber ? (
                      <span className="inline-flex items-center gap-1">
                        #{a.batchNumber}
                        {a.status === 'preparing' ? (
                          <Button size="sm" variant="ghost" onClick={() => unbatch.mutate(a.id)}>
                            Remove
                          </Button>
                        ) : null}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onMessage({ kind: 'signature_request', agreementId: a.id })}
                    >
                      {MESSAGE_LABEL.signature_request}
                    </Button>
                    <Button
                      size="sm"
                      variant={a.form300ReminderDue ? 'outline' : 'ghost'}
                      disabled={!a.periodEnd}
                      onClick={() => onMessage({ kind: 'form300_reminder', agreementId: a.id })}
                    >
                      {MESSAGE_LABEL.form300_reminder}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PanelBody>
    </Panel>
  );
}

function NpiPackPanel({ cycleId }: { cycleId: string }) {
  const pack = useQuery<NpiPack>(
    ['stipend-npi', cycleId],
    async () => (await api.get(`/stipends/cycles/${cycleId}/npi-pack`)).data.data,
  );
  return (
    <Panel className="mb-5">
      <PanelHeader title="NPI pack" note="everything waiting on the NPI" />
      <PanelBody>
        {pack.isLoading ? (
          <SkeletonRows rows={3} />
        ) : pack.error ? (
          <ErrorNotice>Could not load the NPI pack.</ErrorNotice>
        ) : !pack.data?.groups.length ? (
          <p className="text-small text-ink-2">Nothing is waiting on the NPI.</p>
        ) : (
          pack.data.groups.map((g) => (
            <div key={g.stepKey} className="mb-4">
              <div className="font-semibold text-table mb-1">
                {g.label} · {g.entries.length}
              </div>
              <ul className="text-small">
                {g.entries.map((e, i) => (
                  <li key={i} className={`py-1 border-b border-line ${e.overdue ? 'text-ink font-semibold' : ''}`}>
                    {e.fellows.filter(Boolean).join(', ')}
                    {e.supervisor ? <span className="text-ink-2"> · supervisor {e.supervisor}</span> : null}
                    {e.institution ? <span className="text-ink-2"> · {e.institution}</span> : null}
                    {e.amountCents ? <span className="text-ink-2"> · {money(e.amountCents)}</span> : null}
                    {e.dueAt ? <span className="text-ink-2"> · due {dateShort(e.dueAt)}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </PanelBody>
    </Panel>
  );
}

export default function Stipends() {
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [waitingOn, setWaitingOn] = useState('');
  const [open, setOpen] = useState<string | null>(params.get('participant'));
  const [message, setMessage] = useState<MessageTarget | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const cycles = useQuery<ProgramCycleList>(
    'stipend-cycles',
    async () => (await api.get<DataResponse<ProgramCycleList>>('/programs/fellows/cycles')).data.data,
  );
  const cycleId = params.get('cycle') ?? cycles.data?.cycles[0]?.id ?? null;
  useEffect(() => {
    if (!params.get('cycle') && cycles.data?.cycles[0])
      setParams({ cycle: cycles.data.cycles[0].id }, { replace: true });
  }, [cycles.data, params, setParams]);

  const grid = useQuery<StipendGrid>(
    ['stipends', cycleId, waitingOn],
    async () =>
      (await api.get(`/stipends/cycles/${cycleId}`, { params: { waitingOn: waitingOn || undefined } })).data.data,
    { enabled: Boolean(cycleId) && mutable, keepPreviousData: true },
  );
  const counts = useQuery<StipendCounts>(
    ['stipend-counts', cycleId],
    async () => (await api.get(`/stipends/cycles/${cycleId}/summary`)).data.data,
    {
      enabled: Boolean(cycleId) && !mutable,
    },
  );
  const refresh = () => {
    queryClient.invalidateQueries('stipends');
    queryClient.invalidateQueries('stipend-npi');
  };
  const byTemplate = useMemo(() => {
    const out: Record<string, StipendRow[]> = {};
    for (const r of grid.data?.rows ?? []) (out[r.templateKey] ??= []).push(r);
    return out;
  }, [grid.data]);
  const openRow = grid.data?.rows.find((r) => r.participantId === open) ?? null;

  const download = async () => {
    setDownloadError(null);
    try {
      const res = await api.get(`/stipends/cycles/${cycleId}/affiliations.csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stipend-affiliations-${grid.data?.cycle.label ?? 'cohort'}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setDownloadError(errorMessage(err, 'Could not build the affiliation list.'));
    }
  };

  return (
    <>
      <PageHeader
        title="Stipends"
        sub="Each Fellow's stipend as a checklist: route from the institution form, Transfer of Funds agreements (one per supervisor), batches of three for Research Services, the NPI pack and the ledger line. Banking details are never entered here; steps only record that they were sent."
      />
      <div className="flex flex-wrap gap-2 mb-5">
        {cycles.data?.cycles.map((c) => (
          <Button
            key={c.id}
            size="sm"
            variant={c.id === cycleId ? 'dark' : 'outline'}
            onClick={() => {
              setParams({ cycle: c.id });
              setOpen(null);
            }}
          >
            Fellows {c.label}
          </Button>
        ))}
      </div>
      {!mutable ? (
        counts.data ? (
          <Panel>
            <PanelHeader title="Stipends" />
            <PanelBody className="text-table">
              {counts.data.enabled ? '' : 'Stipends are off for this cohort. '}
              {fmt(counts.data.fellows)} Fellow(s) on a stipend checklist, {fmt(counts.data.complete)} complete.
              {Object.entries(counts.data.byRoute).map(([k, n]) => (
                <div key={k} className="text-ink-2">
                  {label(k)}: {n}
                </div>
              ))}
            </PanelBody>
          </Panel>
        ) : counts.error ? (
          <ErrorNotice>Could not load stipend counts.</ErrorNotice>
        ) : (
          <SkeletonRows rows={2} />
        )
      ) : grid.isLoading ? (
        <SkeletonRows rows={8} />
      ) : grid.error ? (
        <ErrorNotice>{errorMessage(grid.error, 'Could not load stipends.')}</ErrorNotice>
      ) : grid.data ? (
        <>
          <Settings key={grid.data.cycle.id} grid={grid.data} onSaved={refresh} />
          {grid.data.outbox ? (
            <div className="mb-4">
              <InfoNotice>Outbox mode: messages are logged, not delivered.</InfoNotice>
            </div>
          ) : null}
          <Panel className="mb-5">
            <PanelHeader title={`Cohort ${grid.data.cycle.label}`} note={`${grid.data.rows.length} Fellow(s)`}>
              <select className={selectClass} value={waitingOn} onChange={(e) => setWaitingOn(e.target.value)}>
                {WAITING_ON.map((w) => (
                  <option key={w.value} value={w.value}>
                    {w.label}
                  </option>
                ))}
              </select>
              <Button size="sm" variant="outline" onClick={download}>
                Affiliation list (CSV)
              </Button>
            </PanelHeader>
            <PanelBody>
              {downloadError ? <ErrorNotice>{downloadError}</ErrorNotice> : null}
              {!grid.data.rows.length ? (
                <EmptyState title="No stipend checklists">
                  {grid.data.cycle.settings.stipendsEnabled
                    ? 'Fellows get one when they are selected or active.'
                    : 'Turn stipends on for this cohort above.'}
                </EmptyState>
              ) : (
                Object.entries(ROUTE_TEMPLATE)
                  .filter(([, t]) => byTemplate[t]?.length)
                  .map(([route, t]) => (
                    <div key={t} className="mb-6">
                      <div className="font-semibold text-table mb-2">
                        {grid.data?.routes[route]} · {byTemplate[t].length}
                      </div>
                      <Grid
                        grid={grid.data as StipendGrid}
                        templateKey={t}
                        rows={byTemplate[t]}
                        onOpen={(r) => setOpen(r.participantId)}
                      />
                    </div>
                  ))
              )}
              <p className="text-small text-ink-2">
                ● done · ○ open · · waiting on earlier steps · – skipped · ! late. Click a Fellow for their steps.
              </p>
            </PanelBody>
          </Panel>
          <Agreements grid={grid.data} onChanged={refresh} onMessage={setMessage} />
          <NpiPackPanel cycleId={grid.data.cycle.id} />
        </>
      ) : null}
      {openRow && grid.data ? (
        <FellowPanel
          row={openRow}
          grid={grid.data}
          onClose={() => setOpen(null)}
          onChanged={refresh}
          onMessage={setMessage}
        />
      ) : null}
      {message ? <StipendMessage {...message} onClose={() => setMessage(null)} onSent={refresh} /> : null}
    </>
  );
}
